import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Controller, useController, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  AutocompleteRenderInputParams,
  Box,
  Divider,
  DragAndDrop,
  FormInputText,
  Text,
  TimePickerV2,
  Toggle,
} from '@hopskipdrive/react-components';
import { TimeFieldProps } from '@mui/x-date-pickers/TimeField';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { includes, uniqBy } from 'lodash';

import { defaultWaypoint, defaultWaypointAnchor, DROPOFF, PICKUP } from '../../../constants';
import { useLocationOptions } from '../../../hooks/useLocationOptions';
import { useRiderDetails } from '../../../hooks/useRiderDetails';
import { useRiderLocationOptions } from '../../../hooks/useRiderLocationOptions';
import { useTimezone } from '../../../hooks/useTimezone';
import { SelectLocation } from '../../../locations/features/MapPins/SelectLocation';
import { colorTokens } from '../../../shared/components/variables';
import { LocationAccordion, LocationAccordionSkeleton } from '../../components/LocationAccordion';
import { RiderAccordion } from '../../components/RiderAccordion';
import { SelectRider } from '../../components/SelectRider';
import { useRideEditor } from '../../hooks/useRideEditor';

import { getWaypointFormValues, helpers } from './helpers';
import { RiderAssistantWaypoint } from './RiderAssistantWaypoint';
import { useAnchorTimeField } from './useAnchorTimeField';
import { useSetUpLocations } from './useSetUpLocations';
import { useValidatedWaypoints } from './useValidatedWaypoints';
import { RiderAssistant, RiderContainer, Timeline, TimelineGrid } from './Waypoints.styles';

export const Waypoints = ({
  rideDetailsDefault,
  isSeries,
}: {
  rideDetailsDefault: RideFormType;
  isSeries: boolean;
}) => {
  const { locations, debounceHandleQuery, locationOptions, saveNewLocation, updateLocation, addMissingLocations } =
    useLocationOptions();
  const {
    waypointsOnMap,
    validatedWaypoints,
    updateWaypointsOnMap,
    swapWaypointPositionOnMap,
    rideDetails,
    getRiderOptions,
    updateAnchorTime,
    routeEstimates,
    updateTripTemplateForSmartPooling,
    suggestionData,
  } = useRideEditor();

  const { savedRiders, ridersOnRide, deboucedQueryRiders, addRiderToRide, updateRiderNotes, removeRidersFromRide } =
    getRiderOptions();
  const { associatedLocationOptions, setRiderSlugs, queryRiderLocations } = useRiderLocationOptions();
  const { mergeDateTime } = useTimezone();
  const { reset, control, register, getValues, setValue, formState } = useFormContext();
  const { errors } = formState;
  const { field: timeField } = useController({
    control,
    name: 'time',
    defaultValue: rideDetailsDefault.time ?? '',
  });
  const anchorTimeField = useAnchorTimeField(timeField.value ?? '', timeField.onChange, formState.defaultValues);
  const timePickerFieldProps: Pick<TimeFieldProps<true>, 'unstableFieldRef' | 'readOnly'> = {
    unstableFieldRef: (field) =>
      timeField.ref(
        field
          ? {
              focus: () => {
                // Select All clearing removes section nodes until React renders them again.
                flushSync(() => field.setSelectedSections(0));
                field.focusField(0);
              },
            }
          : null
      ),
  };
  const [riderDefaultsChecked, setRiderDefaultsChecked] = useState(false);

  // this is use to prepopulate the rider details and locations on the form
  const location = useLocation();
  const { riderSlug } = location?.state || '';
  const { riderDetails: riderFromLocationState, isFetching: isFetchingRiderDetails } = useRiderDetails(riderSlug);
  // Use a stable date reference for fallback to avoid unnecessary effect runs
  const stableDateRef = useRef(new Date()).current;

  useSetUpLocations({ rideDetails, addMissingLocations, locations });
  const { fields, insert, append, remove, move } = useFieldArray({
    control,
    name: 'waypointsAttributes',
  });

  const rideType = useWatch({
    control,
    name: 'timeAnchor',
    defaultValue: rideDetailsDefault.timeAnchor || DROPOFF,
  });

  const watchWaypoints = useWatch({
    control,
    name: 'waypointsAttributes',
  });

  const watchTime = useWatch({
    control,
    name: 'time',
    defaultValue: rideDetailsDefault.time,
  });
  const watchAnchorTime = useWatch({
    control,
    name: 'anchorTime',
    defaultValue: rideDetailsDefault.anchorTime,
  });

  const watchTripTemplateAttributes = useWatch({
    control,
    name: 'tripTemplateAttributes',
    defaultValue: rideDetailsDefault.tripTemplateAttributes,
  });

  const repeatingDays =
    watchTripTemplateAttributes &&
    ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].filter(
      (day) => watchTripTemplateAttributes[day]
    );

  useValidatedWaypoints(watchWaypoints);

  const onRemoveRiders = (riders) => {
    if (riders && riders.length) {
      const filteredWaypoints = watchWaypoints.map((waypoint) => ({
        ...waypoint,
        passengerIds: waypoint.passengerIds?.filter((id) => !riders.includes(id)),
      }));

      setValue('waypointsAttributes', filteredWaypoints);

      updateWaypointsOnMap(
        waypointsOnMap.map((nextWaypoint) => {
          return {
            ...nextWaypoint,
            passengerIds: nextWaypoint.passengerIds?.filter((id) => !riders.includes(id)),
          };
        })
      );
      removeRidersFromRide(riders);
    }
  };

  useEffect(() => {
    if (watchTripTemplateAttributes) {
      const { startDate, endDate, scheduleVariances, ...days } = watchTripTemplateAttributes || {};

      updateTripTemplateForSmartPooling({ startDate, endDate, scheduleVariances, ...days });
    } else {
      updateTripTemplateForSmartPooling();
    }
  }, [watchTime, watchTripTemplateAttributes?.startDate, watchTripTemplateAttributes?.endDate, repeatingDays?.length]);

  const getDisabledOptions = (index) => {
    let disabledOptions = [] as string[];

    const isAnchorWaypoint = helpers[rideType].isAnchor(index, watchWaypoints.length);
    const isNextToAnchorWaypoint = helpers[rideType].isNextToAnchor(index, watchWaypoints.length);

    // when it's the anchor waypoint, it should disable the location next to it
    if (isAnchorWaypoint) {
      rideType === PICKUP
        ? disabledOptions.push(watchWaypoints[1].location.placeId)
        : disabledOptions.push(watchWaypoints[watchWaypoints.length - 2]?.location.placeId);

      return disabledOptions;
    }

    // when it's a waypoint next to anchor, it should disable all selected locations
    if (isNextToAnchorWaypoint) {
      return (disabledOptions = watchWaypoints.map((waypoint) => waypoint.location.placeId));
    }

    // when it's a non-anchor waypoint and it's not next to anchor, it should
    // disable all non-anchor selected locations
    return (disabledOptions = watchWaypoints.map((wp) => (!wp.anchor ? wp.location.placeId : '')));
  };

  useEffect(() => {
    if (watchTime && watchTime !== rideDetailsDefault.time) {
      // we should allow the user to select time first
      // so the date portion can be a new Date() if anchorTime is empty

      const formattedAnchorTime = mergeDateTime(watchAnchorTime || stableDateRef, watchTime);

      updateAnchorTime(formattedAnchorTime);
    }
  }, [watchTime, watchAnchorTime]);

  useEffect(() => {
    // Sync later source updates, including edits and smartpooling suggestions.
    if (rideDetails) {
      reset({ waypointsAttributes: getWaypointFormValues(rideDetails.waypoints) });
    }
  }, [rideDetails]);

  useEffect(() => {
    ridersOnRide.length && setRiderSlugs(ridersOnRide.map((rider) => rider.id || ''));
  }, [ridersOnRide]);

  useEffect(() => {
    if (riderFromLocationState) {
      // if there is a rider set in the state, add them to the ride and the waypoints
      addRiderToRide({
        ...riderFromLocationState,
        group: 'Saved riders',
        label: riderFromLocationState.firstName || '',
        name: `${riderFromLocationState.firstName} ${riderFromLocationState.lastName}`,
        driverGenderPreferences: riderFromLocationState.driverGenderPreferences || [],
        id: riderFromLocationState.slug,
        booster: riderFromLocationState.boosterSeat,
      });
      setValue(`waypointsAttributes.${0}.passengerIds`, [riderSlug]);
      setValue(`waypointsAttributes.${1}.passengerIds`, [riderSlug]);
    }
  }, [riderFromLocationState]);

  useEffect(() => {
    if (!riderDefaultsChecked && riderSlug && associatedLocationOptions) {
      const primaryHome = associatedLocationOptions.find(
        (associatedLocation) =>
          associatedLocation.locationType === 'residence' &&
          associatedLocation.primary &&
          associatedLocation.riderSlug === riderSlug
      );
      const primarySchool = associatedLocationOptions.find(
        (associatedLocation) =>
          associatedLocation.locationType === 'school' &&
          associatedLocation.primary &&
          associatedLocation.riderSlug === riderSlug
      );

      if (primaryHome) {
        setValue(`waypointsAttributes.${0}.location`, {
          address: primaryHome?.address,
          name: primaryHome?.name,
          placeId: primaryHome?.placeId,
          preciseLocation: primaryHome?.preciseLatLng,
          id: primaryHome?.id,
        });
      }
      if (primarySchool) {
        setValue(`waypointsAttributes.${1}.location`, {
          address: primarySchool?.address,
          name: primarySchool?.name,
          placeId: primarySchool?.placeId,
          preciseLocation: primarySchool?.preciseLatLng,
          id: primarySchool?.id,
        });
      }
      if (primaryHome || primarySchool) {
        updateWaypointsOnMap([
          ...(primaryHome
            ? [
                {
                  anchor: false,
                  location: { placeId: primaryHome.placeId },
                  address: primaryHome.address,
                  latLng: primaryHome.preciseLatLng || primaryHome.latLng,
                  type: rideType,
                  passengerIds: [riderSlug],
                },
              ]
            : [{ ...defaultWaypoint, location: undefined }]),
          ...(primarySchool
            ? [
                {
                  anchor: true, // default type is Dropoff on page load
                  location: { placeId: primarySchool.placeId },
                  address: primarySchool.address,
                  latLng: primarySchool.preciseLatLng || primarySchool.latLng,
                  type: rideType,
                  passengerIds: [riderSlug],
                },
              ]
            : [{ ...defaultWaypointAnchor, location: undefined }]),
        ]);
      }
      setRiderDefaultsChecked(true);
    } else if (!riderSlug) {
      setRiderDefaultsChecked(true);
    }
  }, [associatedLocationOptions, riderSlug]);

  return (
    <>
      <TimelineGrid style={{ marginBottom: '32px' }}>
        <Box display="flex" alignItems="center">
          <PageHeading variant="h2">{'Route'}</PageHeading>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" data-testid="time-anchor-toggle">
          <Controller
            control={control}
            name="timeAnchor"
            defaultValue={rideDetailsDefault.timeAnchor}
            render={({ field: { onChange, value } }) => (
              <Toggle
                value={value}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    return;
                  }
                  onChange(newValue);
                  resetWaypoints(newValue);
                }}
                exclusive
              >
                <Toggle.Option value={PICKUP}>{'Pickup route'}</Toggle.Option>
                <Toggle.Option value={DROPOFF}>{'Drop-off route'}</Toggle.Option>
              </Toggle>
            )}
          />
        </Box>
      </TimelineGrid>
      <Timeline>
        {validatedWaypoints[0]?.isRiderAssistant && (
          <RiderAssistantWaypoint
            num={1}
            rideLabel={helpers[rideType].getRALabel()}
            location={locations.find((item) => item.id?.toString() === validatedWaypoints[0].location.id?.toString())}
            type={rideType}
            riderAssistants={[]}
          />
        )}
        <DragAndDrop
          items={fields}
          handleDragEnd={(event) => {
            const { active, over } = event;

            if (!over || helpers[rideType].isAnchor(over.data.current?.sortable.index, watchWaypoints.length)) {
              return;
            }
            move(active.data.current?.sortable.index, over.data.current?.sortable.index);
            swapWaypointPositionOnMap(active.data.current?.sortable.index, over.data.current?.sortable.index);
          }}
        >
          {fields.map((item, index) => {
            // we use the values from the watch because the `fields` value is just the defaultValues
            const values = watchWaypoints[index];

            const isDropoffForLocation = helpers[rideType].getWaypointType(index, fields.length) === DROPOFF;
            const isAnchor = helpers[rideType].isAnchor(index, fields.length);
            const disableItem = isAnchor || !helpers[rideType].isDraggable(index, fields.length);
            // this is useful for view mode to easily format dates
            const waypointDetails = rideDetails?.waypoints[index];

            const isSmartPoolingSuggestion =
              !isAnchor &&
              !!suggestionData?.waypointsAttributes.find(
                (waypoint) => waypoint.location.placeId === values?.location?.placeId
              );

            return (
              <div key={`waypointsAttributes-${item.id}`}>
                <DragAndDrop.SortableItem itemId={item.id} isDisabled={disableItem}>
                  <Timeline.Item
                    dotContent={
                      <DragAndDrop.DragHandle itemId={item.id} isDisabled={disableItem}>
                        {isDropoffForLocation ? (
                          <Timeline.NumberDotWithActions
                            num={validatedWaypoints[0]?.isRiderAssistant ? index + 2 : index + 1}
                            highlight={isSmartPoolingSuggestion}
                            onRemove={() => {
                              onRemoveRiders(values?.passengerIds);
                              remove(index);
                              updateWaypointsOnMap(
                                waypointsOnMap.filter((_, waypointIndex) => waypointIndex !== index)
                              );
                            }}
                            disableAction={disableItem}
                          />
                        ) : (
                          <Timeline.LocationDotWithActions
                            num={validatedWaypoints[0]?.isRiderAssistant ? index + 2 : index + 1}
                            highlight={isSmartPoolingSuggestion}
                            onRemove={() => {
                              onRemoveRiders(values?.passengerIds);
                              remove(index);
                              updateWaypointsOnMap(
                                waypointsOnMap.filter((_, waypointIndex) => waypointIndex !== index)
                              );
                            }}
                            disableAction={disableItem}
                          />
                        )}
                      </DragAndDrop.DragHandle>
                    }
                    connector={isAnchor ? <Timeline.SolidConnector /> : <Timeline.DottedConnector />}
                  >
                    <div
                      role={isAnchor ? undefined : 'group'}
                      aria-labelledby={isAnchor ? undefined : `waypoint-time-label-${index}`}
                    >
                      <Box mb={5} display="flex" flexDirection="row" justifyContent="space-between">
                        <>
                          <label id={`waypoint-time-label-${index}`} htmlFor={`waypoint-time-${index}`}>
                            <Text textColor={waypointDetails?.actualArrivesAt ? 'secondaryText' : ''}>
                              {helpers[rideType].getWaypointTitle(index, watchWaypoints.length)}
                            </Text>
                          </label>
                          {!isAnchor && waypointsOnMap[index]?.location ? (
                            <Text>{routeEstimates[index]?.estimatedArrivesAt}</Text>
                          ) : null}
                        </>
                      </Box>
                      <Box
                        mb={5}
                        pb={isAnchor ? 5 : 0}
                        sx={{
                          borderBottom: '1px dashed #DFE4E8',
                          '.error': {
                            fontSize: '12px',
                            fontWeight: 300,
                            color: `${colorTokens.error.dark} !important`,
                          },
                        }}
                      >
                        {isAnchor && (
                          <Box sx={{ width: 180, maxWidth: '100%' }}>
                            <TimePickerV2
                              enableAccessibleFieldDOMStructure
                              localeText={{ fieldMeridiemPlaceholder: () => 'AM' }}
                              name={timeField.name}
                              value={anchorTimeField.value}
                              onChange={anchorTimeField.onChange}
                              slotProps={{
                                field: timePickerFieldProps,
                                textField: {
                                  id: `waypoint-time-${index}`,
                                  sx: {
                                    '& .MuiPickersInputBase-root:not(.Mui-disabled) .MuiPickersSectionList-root': {
                                      color: colorTokens.text.medium,
                                      opacity: 1,
                                    },
                                  },
                                  onBlur: timeField.onBlur,
                                  error: !!errors.time || !!anchorTimeField.error,
                                  helperText:
                                    (errors.time?.message as string) ||
                                    (anchorTimeField.error ? 'Enter a valid time' : ''),
                                  role: 'group',
                                  'aria-labelledby': `waypoint-time-label-${index}`,
                                },
                                popper: { 'aria-labelledby': `waypoint-time-label-${index}` },
                                mobilePaper: { 'aria-labelledby': `waypoint-time-label-${index}` },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      {isFetchingRiderDetails && (
                        <Box mb={4}>
                          <RiderAccordion
                            isFetching={true}
                            rider={undefined}
                            waypointType={helpers[rideType].getWaypointType(index, watchWaypoints.length)}
                            locationId={values?.location?.id}
                            updateRiderNotes={updateRiderNotes}
                            disableRemove={true}
                            onRemove={() => null}
                          />
                        </Box>
                      )}
                      {((validatedWaypoints[0]?.isRiderAssistant && index === watchWaypoints.length - 1) ||
                        (validatedWaypoints[validatedWaypoints.length - 1]?.isRiderAssistant && index === 0)) && (
                        <RiderAssistant data-testid="ra-rider">
                          <span>RA</span>
                          <div>Rider Assistant</div>
                        </RiderAssistant>
                      )}
                      {values?.passengerIds && values?.passengerIds.length
                        ? ridersOnRide
                            .filter((rider) => includes(values.passengerIds, rider.id))
                            ?.map((selectedRider) => (
                              <RiderContainer
                                mb={4}
                                key={`waypointsAttributes-${item.id}-rider-${selectedRider.id}`}
                                $highlight={
                                  !isAnchor &&
                                  suggestionData?.riders.find((rider) => rider.name === selectedRider.name) !==
                                    undefined
                                }
                              >
                                <RiderAccordion
                                  rider={selectedRider}
                                  waypointType={helpers[rideType].getWaypointType(index, watchWaypoints.length)}
                                  locationId={values?.location?.id}
                                  updateRiderNotes={updateRiderNotes}
                                  disableRemove={(rideDetailsDefault?.id && ridersOnRide.length < 2) || false}
                                  onRemove={() => {
                                    onRemoveRiders([selectedRider?.id]);
                                  }}
                                />
                              </RiderContainer>
                            ))
                        : null}
                      {isFetchingRiderDetails ? null : (
                        <Box pb={5} mb={5} sx={{ borderBottom: '1px dashed #DFE4E8' }}>
                          <SelectRider
                            isSeries={isSeries}
                            options={[
                              ...ridersOnRide,
                              ...savedRiders.filter(
                                (savedRider) =>
                                  !includes(
                                    ridersOnRide.map((rider) => rider.id),
                                    savedRider.id
                                  )
                              ),
                            ]}
                            canAddRiders={values?.passengerIds.length < 4}
                            placeholder={helpers[rideType].getRiderInputPlaceholder(index, watchWaypoints.length)}
                            waypointIndex={index}
                            isAnchorWaypoint={isAnchor}
                            rideType={rideType}
                            addRiderToRide={addRiderToRide}
                            deboucedQueryRiders={deboucedQueryRiders}
                            onSelect={() => {
                              updateWaypointsOnMap(
                                waypointsOnMap.map((waypoint, waypointIndex) => ({
                                  ...waypoint,
                                  passengerIds: getValues(`waypointsAttributes.${waypointIndex}.passengerIds`) || [],
                                }))
                              );
                            }}
                          />
                        </Box>
                      )}
                      {values?.location.placeId ? (
                        <LocationAccordion
                          editMode={true}
                          location={values?.location}
                          waypointType={helpers[rideType].getWaypointType(index, watchWaypoints.length)}
                          locations={locations}
                          updateLocation={updateLocation}
                          onRemove={() => {
                            setValue(`waypointsAttributes.${index}.location`, defaultWaypoint.location);

                            updateWaypointsOnMap(
                              waypointsOnMap.map((waypoint, waypointIndex) => {
                                if (index === waypointIndex) {
                                  return {
                                    ...waypoint,
                                    location: undefined,
                                    latLng: '',
                                    estimatedArrivesAt: '',
                                    passengerIds: [],
                                  };
                                }

                                return waypoint;
                              })
                            );
                          }}
                        />
                      ) : null}
                      {!riderDefaultsChecked ? (
                        <LocationAccordionSkeleton />
                      ) : (
                        <SelectLocation
                          id={`waypointsAttributes_${index}_location_address`}
                          data-testid="select-location"
                          hidden={!!values?.location.placeId}
                          options={[
                            ...uniqBy(
                              associatedLocationOptions?.filter((associatedLocation) =>
                                includes(values?.passengerIds, associatedLocation.riderSlug)
                              ),
                              (associatedLocation) => associatedLocation.placeId
                            ),
                            ...locationOptions,
                          ]}
                          disableOptions={getDisabledOptions(index)}
                          saveLocation={saveNewLocation}
                          source="Ride"
                          setInputValue={(selectedLocation) => {
                            // reset locations search on select or input clear
                            values?.passengerIds && queryRiderLocations('');
                            debounceHandleQuery('');
                            if (!selectedLocation?.label) {
                              return '';
                            }
                            const isAnchorWaypoint = helpers[rideType].isAnchor(index, watchWaypoints.length);

                            updateWaypointsOnMap([
                              ...waypointsOnMap.slice(0, index),
                              {
                                anchor: isAnchorWaypoint,
                                location: {
                                  id: selectedLocation.id,
                                  placeId: selectedLocation.placeId,
                                },
                                address: selectedLocation.address,
                                latLng: selectedLocation.preciseLatLng || selectedLocation.latLng,
                                type: helpers[rideType].getWaypointType(index, watchWaypoints.length),
                                passengerIds: values?.passengerIds,
                              },
                              ...waypointsOnMap.slice(index + 1),
                            ]);
                            isAnchorWaypoint && setValue('calendarSlug', selectedLocation.calendar);

                            setValue(`waypointsAttributes.${index}.location.address`, selectedLocation.address);
                            setValue(`waypointsAttributes.${index}.location.name`, selectedLocation.name);
                            setValue(`waypointsAttributes.${index}.location.placeId`, selectedLocation?.placeId);
                            setValue(`waypointsAttributes.${index}.location.id`, selectedLocation?.id);
                            selectedLocation?.preciseLatLng &&
                              setValue(
                                `waypointsAttributes.${index}.location.preciseLocation`,
                                selectedLocation?.preciseLatLng
                              );

                            return selectedLocation.label;
                          }}
                          renderInput={(params: AutocompleteRenderInputParams) => (
                            <FormInputText
                              {...params}
                              variant="outlined"
                              fullWidth
                              placeholder={helpers[rideType].getLocationInputPlaceholder(index, watchWaypoints.length)}
                              inputProps={{
                                ...params.inputProps,
                                'aria-label': helpers[rideType].getLocationInputPlaceholder(
                                  index,
                                  watchWaypoints.length
                                ),
                              }}
                              onChange={(e) => {
                                values.passengerIds && queryRiderLocations(e.target.value);
                                debounceHandleQuery(e.target.value);
                              }}
                              error={
                                !!(errors.waypointsAttributes && errors.waypointsAttributes[index]?.location?.placeId)
                              }
                              helperText={
                                errors.waypointsAttributes &&
                                errors.waypointsAttributes[index]?.location?.placeId?.message
                              }
                            />
                          )}
                        />
                      )}
                      <Divider bgColor="#F0F5FA" opacity={0.5} mt={5} mb={5} />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.location.name`, { value: values?.location.name })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.slug`, { value: values?.slug || '' })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.location.address`, {
                          value: values?.location.address,
                        })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.location.placeId`, {
                          value: values?.location.placeId,
                        })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.anchor`, {
                          value: helpers[rideType].isAnchor(index, watchWaypoints.length),
                        })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.location.id`, { value: values?.location.id })}
                      />
                      <input
                        type="hidden"
                        {...register(`waypointsAttributes.${index}.passengerIds`, { value: values?.passengerIds })}
                      />
                    </div>
                  </Timeline.Item>
                </DragAndDrop.SortableItem>
                {rideType === PICKUP && index + 1 == watchWaypoints.length && watchWaypoints.length < 5 ? (
                  <Timeline.Item key={'waypointsAttributes-add-waypoint'} dotContent={<Timeline.PlusDot />}>
                    <Button
                      color="secondary"
                      fullWidth
                      className="add-waypoint"
                      onClick={() => {
                        append(defaultWaypoint);
                        updateWaypointsOnMap([
                          ...waypointsOnMap,
                          {
                            anchor: false,
                            location: undefined,
                            latLng: '',
                            type: helpers[rideType].getWaypointType(index),
                            passengerIds: [],
                          },
                        ]);
                      }}
                    >
                      {'Add another drop-off'}
                    </Button>
                  </Timeline.Item>
                ) : null}
                {rideType === DROPOFF && index + 1 === watchWaypoints.length - 1 && watchWaypoints.length < 5 ? (
                  <Timeline.Item
                    key={'waypointsAttributes-add-waypoint'}
                    dotContent={<Timeline.PlusDot />}
                    connector={<Timeline.DottedConnector />}
                  >
                    <Button
                      color="secondary"
                      className="add-waypoint"
                      fullWidth
                      onClick={() => {
                        insert(watchWaypoints.length - 1, defaultWaypoint);
                        updateWaypointsOnMap([
                          ...waypointsOnMap.slice(0, -1),
                          {
                            anchor: false,
                            location: undefined,
                            latLng: '',
                            type: helpers[rideType].getWaypointType(index, watchWaypoints.length),
                            passengerIds: [],
                          },
                          ...waypointsOnMap.slice(-1),
                        ]);
                      }}
                    >
                      {'Add another pickup'}
                    </Button>
                  </Timeline.Item>
                ) : null}
              </div>
            );
          })}
        </DragAndDrop>

        {validatedWaypoints[validatedWaypoints.length - 1]?.isRiderAssistant && (
          <RiderAssistantWaypoint
            num={watchWaypoints.length + 1}
            type={rideType}
            rideLabel={helpers[rideType].getRALabel()}
            location={locations.find((item) => item.id?.toString() === validatedWaypoints[0].location.id?.toString())}
            riderAssistants={[]}
          />
        )}
      </Timeline>
    </>
  );
  function resetWaypoints(nextRideType) {
    const currentFormValues = getValues();

    if (currentFormValues.waypointsAttributes.length === 2) {
      const waypoints = currentFormValues.waypointsAttributes;
      const newAnchor = waypoints.filter((wp) => !wp.anchor);
      const newAnchorLocationDetails = locations.filter((loc) => loc.id === newAnchor[0].location.id.toString());
      const newCalendarSlug = newAnchorLocationDetails.length && newAnchorLocationDetails[0].calendar;

      reset({
        ...rideDetailsDefault,
        ...currentFormValues,
        calendarSlug: newCalendarSlug,
        waypointsAttributes: [
          {
            ...waypoints[0],
            anchor: !waypoints[0].anchor,
          },
          {
            ...waypoints[1],
            anchor: !waypoints[1].anchor,
          },
        ],
      });
      waypointsOnMap.length &&
        updateWaypointsOnMap(
          waypointsOnMap.map((waypoint, index) => ({
            ...waypoint,
            anchor: !waypoint.anchor,
            type: helpers[nextRideType].getWaypointType(index, watchWaypoints.length),
            estimatedArrivesAt: null,
          }))
        );
    } else {
      // reset default waypoints(empty locations) on the map
      waypointsOnMap.length &&
        updateWaypointsOnMap(
          rideDetailsDefault.waypointsAttributes.map((waypoint, index) => ({
            anchor: waypoint.anchor,
            location: undefined,
            latLng: '',
            type: helpers[nextRideType].getWaypointType(index, watchWaypoints.length),
            passengerIds: [],
          }))
        );
      // reset the form with the shallow copy of the defaultValues
      reset({
        ...rideDetailsDefault,
        timeAnchor: nextRideType,
        tripTemplateAttributes: currentFormValues.tripTemplateAttributes || rideDetailsDefault.tripTemplateAttributes,
        anchorTime: currentFormValues.anchorTime || rideDetailsDefault.anchorTime,
        time: currentFormValues.time ?? rideDetailsDefault.time,
        waypointsAttributes: rideDetailsDefault.waypointsAttributes.map((wp, i) => {
          const isAnchor = nextRideType === PICKUP && i === 0;

          return {
            ...wp,
            anchor: !!isAnchor,
            passengerIds: isAnchor
              ? currentFormValues.waypointsAttributes.find((waypoint) => waypoint.anchor).passengerIds
              : [],
          };
        }),
      });
    }
  }
};
